import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import config from 'garaje/config/environment';
import installHandsontable from 'garaje/modifiers/install-handsontable';
import type Handsontable from 'handsontable';
import _isEmpty from 'lodash/isEmpty'; // Use lodash version as isBlank from utils doesn't check object emptiness
import { localCopy } from 'tracked-toolbox';

const testing = config.environment === 'test';

interface HandsonTableSignature {
  Args: {
    colHeaders: Handsontable.DefaultSettings['colHeaders'];
    columns: Handsontable.DefaultSettings['columns'];
    registerInstance?: (instance: Handsontable) => void;
    data?: unknown[];
    options?: Handsontable.GridSettings;
    hooks?: {
      afterChange?: (instance: Handsontable) => unknown;
      afterRemoveRow?: (instance: Handsontable) => unknown;
    };
  };
  Element: HTMLDivElement;
}

export default class HandsonTable extends Component<HandsonTableSignature> {
  elementId = guidFor(this);
  @tracked hotInstance?: Handsontable;

  @localCopy('args.hooks', {}) declare hooks: Record<string, (...args: unknown[]) => unknown>;

  get options(): Handsontable.DefaultSettings {
    const options = Object.assign({}, this.args.options, {
      colHeaders: this.args.colHeaders,
      columns: this.args.columns,
    });

    if (this.args.data?.length) {
      options.data = this.args.data;
    }

    return options;
  }

  willDestroy(): void {
    super.willDestroy();

    const hot = this.hotInstance;
    if (hot) hot.destroy();
  }

  revalidateData(): void {
    const hot = this.hotInstance;

    if (!hot) return;

    const data = hot.getData();

    data.forEach((_, i) => {
      if (hot.isEmptyRow(i)) return;

      hot.validateRows([i], () => {});
    });
  }

  @action
  onInstallHandsontable(instance: Handsontable): void {
    this.args.registerInstance?.(instance);
    this.hotInstance = instance;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    if (testing) window.hotInstance = instance;
  }

  @action
  onUpdate(): void {
    // didUpdateAttrs may redraw aggressively; this could remove validation classes
    if (this.hotInstance) this.revalidateData();
  }

  <template>
    <div
      ...attributes
      id="{{this.elementId}}-hot"
      data-test-spreadsheet
      {{installHandsontable this.options this.hooks this.onUpdate this.onInstallHandsontable}}
    >
    </div>
  </template>
}
